import { useTranslation } from "react-i18next";
import { getPlatformConfig } from "@/lib/platform/config";

const BAC_NAME = getPlatformConfig("bac").name;

const sections = [
  {
    headingKey: "home.uk_core.heading_marketplace",
    paragraphs: [
      "{brand} is the UK marketplace for hiring classic, vintage and collector cars from the people who own them. Instead of a fleet company with a handful of vehicles, you are choosing from hundreds of privately owned cars listed by enthusiasts in every part of the country, each one photographed, described and priced by its owner. Established more than a decade ago, the platform now covers everything from a 1930s tourer for a wedding to a modern supercar for a weekend.",
    ],
  },
  {
    headingKey: "home.uk_core.heading_vehicles",
    items: [
      "Vintage and classic wedding cars, chauffeured by the owner on the day",
      "Self-drive classics for a birthday, anniversary or a day in the countryside",
      "American classics and muscle cars, from Cadillacs and Mustangs to Chevrolet Bel Airs",
      "Sports cars and supercars for track days, tours and special occasions",
      "Prom cars, VW campervans, vintage buses and period vehicles for film and television",
    ],
  },
  {
    headingKey: "home.uk_core.heading_hire_type",
    paragraphs: [
      "Each listing states whether the owner offers self-drive hire, chauffeured hire or both. Self-drive is ideal for a gift or a day out and usually runs by the day, with driver age and licence requirements set by the owner. Chauffeured hire is the choice for weddings, proms and events, where the owner drives, plans the route and looks after the car while you enjoy the occasion.",
    ],
  },
  {
    headingKey: "home.uk_core.heading_coverage",
    paragraphs: [
      "Owners are listed throughout England, Scotland, Wales and Northern Ireland. The largest concentrations are around London and the Home Counties, Birmingham and the West Midlands, Manchester and Cheshire, Leeds and Yorkshire, Bristol and the South West, Glasgow and Edinburgh. Search by your postcode or town and the nearest available cars appear first, and many owners are happy to travel further for a wedding or an event.",
    ],
  },
  {
    headingKey: "home.uk_core.heading_occasions",
    paragraphs: [
      "Weddings are the most popular reason to hire a classic car, followed by school proms, milestone birthdays, anniversaries and surprise gifts. Owners also supply cars for film and television productions, advertising shoots, corporate events and classic car tours, and several list unusual vehicles such as vintage double-decker buses and American school buses.",
    ],
  },
  {
    headingKey: "home.uk_core.heading_how_it_works",
    paragraphs: [
      "Search for the type of car and the date you need, compare real photographs and owner-written descriptions, then send a booking request with your times and locations. The owner replies directly with a confirmed price, and payment is taken securely through {brand} once both sides have agreed. There is no membership fee, and the owner sets the price, so you deal with the person who actually knows the car.",
    ],
  },
] as const;

export default function UkCoreEditorial() {
  const { t } = useTranslation();
  return (
    <section className="bg-background py-16" aria-labelledby="uk-core-editorial-heading">
      <div className="mx-auto max-w-4xl space-y-10 px-4 sm:px-6 lg:px-8">
        {sections.map((section, index) => (
          <div key={section.headingKey}>
            <h2
              id={index === 0 ? "uk-core-editorial-heading" : undefined}
              className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
            >
              {t(section.headingKey, { brand: BAC_NAME })}
            </h2>
            {"paragraphs" in section && section.paragraphs.map((paragraph) => (
              <p key={paragraph} className="mt-4 text-base leading-7 text-muted-foreground">
                {paragraph.replaceAll("{brand}", BAC_NAME)}
              </p>
            ))}
            {"items" in section && (
              <ul className="mt-4 list-disc space-y-2 pl-6 text-base leading-7 text-muted-foreground">
                {section.items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}